import React, {
  useMemo, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Field, untouch } from 'redux-form';
import { t } from 'i18next';
import screenResolution from '../../../../../utils/screen-resolution';
import NestedDropdown from '../../../../../lib/ui/form_elements/NestedDropdown';
import Dropdown from '../../../../../lib/ui/form_elements/Dropdown';
import TextInput from '../../../../../lib/ui/form_elements/TextInput';
import { Error as FieldError } from '../../../../../lib/ui/form_elements/TextInput/css';
import Checkbox from '../../../../../lib/ui/form_elements/Checkbox';
import {
  Container,
  DangerousGoodsCheckboxContainer,
  DangerousGoodsCheckboxGroup,
  DangerousGoodsContainer,
  DangerousGoodsFields,
  DangerousGoodsQuantityFields,
  DeleteItemButton,
  FieldsRow1,
  FieldsRow2,
  FieldsRow3,
  ItemBody,
  ItemHeader,
  ItemTitle,
  MeasurementsContainer,
} from './css';
import Tooltip from '../../../../../lib/ui/form_elements/Tooltip';
import {
  UnitType, maxWeightImperial, maxWeightMetric, PackagingGroups, PackagingTypes,
} from '../../../../../utils/enums';
import {
  maxChar80,
  isRequired,
  isValidDigitInput,
  isValidDescription,
  validateIntInRange,
  isValidWeight,
  maxChar75,
  maxChar6,
  minChar4,
  prohibitedClassNumber,
} from '../../../../../utils/validator';
import { digitOnly, numbersAndTwoDecimals, shippingNameNormalize } from '../../../../../utils/mask';

const SHIPPING_NAME_INVALID = /[^a-zA-Z\d\s,.\-']/;

const ShippingNameInput = (props) => {
  const [pasteError, setPasteError] = useState(null);
  const isPasting = useRef(false);

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text');

    if (SHIPPING_NAME_INVALID.test(pasted)) {
      isPasting.current = true;
      setPasteError(t('ShippingNameError'));
    } else {
      isPasting.current = false;
      setPasteError(null);
    }
  };

  const handleChange = (...args) => {
    if (isPasting.current) {
      isPasting.current = false;
    } else {
      setPasteError(null);
    }

    props.input.onChange(...args);
  };

  return (
    <TextInput
      {...props}
      input={{ ...props.input, onChange: handleChange, onPaste: handlePaste }}
      errorMessage={pasteError || undefined}
    />
  );
};

ShippingNameInput.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
  }).isRequired,
};

const isBetween1And999 = validateIntInRange(1, 999);

const isBetween1And999999 = validateIntInRange(1, 999999);

const DG_FIELDS = [
  'shippingName',
  'unNumber',
  'totalQuantity',
  'totalQuantityUnits',
  'numberPackRequireLabel',
  'dgPrimaryClass',
  'dgSubsidiaryClass',
  'dgPackagingGroup',
  'dgPackagingType',
];

const Item = ({
  // TODO: itemLimitedQuantity prop temporarily removed from destructuring, LQ disabled until backend (TruckMate) supports it.
  // item, index, onRemove, itemDangerous, itemLimitedQuantity,
  item, index, onRemove, itemDangerous,
  selectedUnits, commoditiesList, usShipment, density,
  fieldsShouldBeBlocked, avoidChanges, measurementList,
  dispatch, formName, commodityType,
}) => {
  const resolution = screenResolution();

  const commodityTypeName = commodityType && (typeof commodityType === 'object' ? commodityType.name : commodityType);
  const isDGClass = commodityTypeName && commodityTypeName.toLowerCase().includes('dg');
  const showDGClassError = usShipment && itemDangerous && !isDGClass;

  const inputSkid = measurementList.SkidQty;
  const inputPiece = measurementList.PieceQty;

  const isImperialUnits = (selectedUnits && selectedUnits.indexOf(t('BtnImperial')) !== -1);

  const commodities = commoditiesList.children.map((category) => ({
    value: category.title.value.toLowerCase(),
    children: category.children.map((commodity) => ({
      code: commodity.code.value || undefined,
      value: commodity.description.value.toLowerCase(),
    })),
  }));

  const validWeightValidator = useMemo(() => {
    const isImperialUnit = selectedUnits === t(UnitType.Imperial);
    let maxWeight;
    if (usShipment) {
      maxWeight = isImperialUnit ? maxWeightImperial.US : maxWeightMetric.US;
    } else {
      maxWeight = isImperialUnit ? maxWeightImperial.nonUS : maxWeightMetric.nonUS;
    }

    return isValidWeight(maxWeight, isImperialUnit);
  }, [selectedUnits]);

  const handleRemoveItem = (e) => {
    e.preventDefault();
    onRemove(index);
  };

  const isSkidOptionalField = !inputSkid && !inputPiece ? [isBetween1And999, isRequired] : [isBetween1And999];
  const isPieceOptionalField = !inputSkid && !inputPiece ? [isBetween1And999, isRequired] : [isBetween1And999];

  const prevItemDangerous = useRef(itemDangerous);
  useEffect(() => {
    if (itemDangerous && !prevItemDangerous.current && dispatch && formName) {
      const fieldNames = DG_FIELDS.map((f) => `${item}.${f}`);
      dispatch(untouch(formName, ...fieldNames));
    }

    prevItemDangerous.current = itemDangerous;
  }, [itemDangerous]);

  return (
    <Container>
      <ItemHeader>
        <ItemTitle>
          {t('Item')}
          {' '}
          {`${index + 1}`}
        </ItemTitle>
        <DeleteItemButton
          onClick={(fieldsShouldBeBlocked) ? () => avoidChanges(t('Delete')) : handleRemoveItem}
        >
          {t('Delete')}
        </DeleteItemButton>
      </ItemHeader>
      <ItemBody>

        <FieldsRow1>

          <Field
            name={`${item}.commodityType`}
            label={t('CommodityType')}
            component={NestedDropdown}
            resolution={resolution}
            options={commodities}
            placeholder={t('Select')}
            disabled={fieldsShouldBeBlocked}
            validate={usShipment ? [isRequired] : null}
            search
            optional={!usShipment}
          />

          <Field
            name={`${item}.description`}
            label={t('Description')}
            maxLength={75}
            component={TextInput}
            validate={[isRequired, maxChar75]}
          />

          {showDGClassError && (
            <FieldError style={{ gridColumn: '1', gridRow: '2', margin: '-20px 0 0 0' }}>
              {t('DGClassNote')}
            </FieldError>
          )}
        </FieldsRow1>

        {
                    (usShipment)
                      ? (
                        <FieldsRow3>
                          <Field
                            name={`${item}.itemNMFC`}
                            label={t('NmfcCode')}
                            component={TextInput}
                            maxLength={6}
                            validate={[isBetween1And999999]}
                            optional
                          />
                        </FieldsRow3>
                      )
                      : <FieldsRow3 />
                }

        <FieldsRow2>
          <Field
            name={`${item}.SkidQty`}
            label={t('SkidQty')}
            component={TextInput}
            maxLength={3}
            validate={isSkidOptionalField}
            disabled={fieldsShouldBeBlocked}
          />
          <Field
            name={`${item}.PieceQty`}
            label={t('PieceQty')}
            component={TextInput}
            maxLength={3}
            validate={isPieceOptionalField}
            disabled={fieldsShouldBeBlocked}
          />

        </FieldsRow2>

        <MeasurementsContainer>
          <Field
            name={`${item}.itemLength`}
            label={`${t('ItemLength')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
            disabled={fieldsShouldBeBlocked}
          />
          <Field
            name={`${item}.itemWidth`}
            label={`${t('ItemWidth')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
            disabled={fieldsShouldBeBlocked}
          />
          <Field
            name={`${item}.itemHeight`}
            label={`${t('ItemHeight')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
            disabled={fieldsShouldBeBlocked}
          />
          <Field
            name={`${item}.itemWeight`}
            label={`${t('TotalWeight')} (${selectedUnits === t(UnitType.Imperial) ? 'lb' : 'kg'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired, validWeightValidator]}
            normalize={numbersAndTwoDecimals}
            disabled={fieldsShouldBeBlocked}
          />
        </MeasurementsContainer>

        <DangerousGoodsContainer>
          <DangerousGoodsCheckboxContainer>
            {(density)
              ? <p>{`${t('VolumeDensity')}: ${density} ${(isImperialUnits) ? t('VolumeDensityImperial') : t('VolumeDensityMetric')}`}</p> : <p />}
            <DangerousGoodsCheckboxGroup>
              <Field
                name={`${item}.itemDangerous`}
                component={Checkbox}
                label={t('DangerousGoods')}
                tooltip={<Tooltip message={t('TipDangerousGoods')} />}
                disabled={fieldsShouldBeBlocked}
              />
              {/* TODO: LQ checkbox temporarily disabled - not supported by backend (TruckMate) yet.
              {itemDangerous && (
                <Field
                  name={`${item}.itemLimitedQuantity`}
                  component={Checkbox}
                  label={t('LimitedQuantity')}
                  tooltip={<Tooltip message={t('TipLimitedQuantity')} />}
                  disabled={fieldsShouldBeBlocked}
                />
              )}
              */}
            </DangerousGoodsCheckboxGroup>
          </DangerousGoodsCheckboxContainer>

          {itemDangerous

                        && (
          // TODO: LQ display logic temporarily disabled - not supported by backend (TruckMate) yet.
          // Was: <DangerousGoodsFields style={itemLimitedQuantity ? { display: 'none' } : undefined}>
                        <DangerousGoodsFields>
                          <Field
                            name={`${item}.shippingName`}
                            component={ShippingNameInput}
                            label={t('ShippingName')}
                            maxLength={80}
                            normalize={shippingNameNormalize}
                            validate={[isRequired, isValidDescription, maxChar80]}
                            // LQ validation bypass temporarily disabled, not supported by backend (TruckMate) yet. Was:
                            // validate={itemLimitedQuantity ? [maxChar250, isValidDescription] : [maxChar250, isRequired, isValidDescription]}
                          />
                          <Field
                            name={`${item}.unNumber`}
                            component={TextInput}
                            label={t('UNNumber')}
                            normalize={digitOnly}
                            maxLength={6}
                            minLength={4}
                            validate={[isValidDigitInput, isRequired, maxChar6, minChar4]}
                            // LQ validation bypass temporarily disabled, not supported by backend (TruckMate) yet. Was:
                            // validate={itemLimitedQuantity ? [isDigitLength4, isValidDigitInput] : [isDigitLength4, isValidDigitInput, isRequired]}
                          />
                          <DangerousGoodsQuantityFields>
                            <Field
                              name={`${item}.totalQuantity`}
                              component={TextInput}
                              label={t('TotalQuantity')}
                              maxLength={6}
                              normalize={digitOnly}
                              validate={[isRequired, isValidDigitInput]}
                              // LQ validation bypass temporarily disabled, not supported by backend (TruckMate) yet. Was:
                              // validate={itemLimitedQuantity ? [isValidDigitInput] : [isRequired, isValidDigitInput]}
                            />
                            <Field
                              name={`${item}.totalQuantityUnits`}
                              label={t('QuantityUnits')}
                              component={Dropdown}
                              options={[{ value: 'KG' }, { value: 'L' }]}
                              validate={[isRequired]}
                              // LQ validation bypass temporarily disabled, not supported by backend (TruckMate) yet. Was:
                              // validate={itemLimitedQuantity ? [] : [isRequired]}
                            />
                          </DangerousGoodsQuantityFields>
                          <Field
                            name={`${item}.numberPackRequireLabel`}
                            component={TextInput}
                            maxLength={3}
                            label={t('NumberLabelsNeeded')}
                          />
                          <Field
                            name={`${item}.dgPrimaryClass`}
                            component={TextInput}
                            label={t('PrimaryClass')}
                            validate={[isRequired, prohibitedClassNumber]}
                          />
                          <Field
                            name={`${item}.dgSubsidiaryClass`}
                            component={TextInput}
                            label={t('SubsidiaryClass')}
                            validate={[prohibitedClassNumber]}
                            optional
                          />
                          <Field
                            name={`${item}.dgPackagingGroup`}
                            component={Dropdown}
                            label={t('PackagingGroup')}
                            options={[
                              { value: PackagingGroups.I.value, label: PackagingGroups.I.name },
                              { value: PackagingGroups.II.value, label: PackagingGroups.II.name },
                              { value: PackagingGroups.III.value, label: PackagingGroups.III.name },
                              { value: PackagingGroups.NotApplicable.value, label: PackagingGroups.NotApplicable.name },
                            ]}
                            validate={[isRequired]}
                          />
                          <Field
                            name={`${item}.dgPackagingType`}
                            component={Dropdown}
                            label={t('PackagingType')}
                            options={Object.values(PackagingTypes).map((pt) => ({ value: pt.value, label: t(pt.labelKey) }))}
                            validate={[isRequired]}
                          />
                        </DangerousGoodsFields>
                        )}
        </DangerousGoodsContainer>
      </ItemBody>
    </Container>
  );
};

Item.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  onRemove: PropTypes.func,
  itemDangerous: PropTypes.bool,
  // TODO: itemLimitedQuantity PropType temporarily removed - LQ disabled until backend (TruckMate) supports it.
  // itemLimitedQuantity: PropTypes.bool,
  selectedUnits: PropTypes.string,
  commoditiesList: PropTypes.shape({
    children: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.shape({
          value: PropTypes.string,
        }),
        children: PropTypes.arrayOf(
          PropTypes.shape({
            code: PropTypes.shape({
              value: PropTypes.string,
            }),
            value: PropTypes.string,
          }),
        ),
      }),
    ),
  }),
  usShipment: PropTypes.bool,
  density: PropTypes.number,
  fieldsShouldBeBlocked: PropTypes.bool,
  avoidChanges: PropTypes.func,
  measurementList: PropTypes.shape({
    SkidQty: PropTypes.bool,
    PieceQty: PropTypes.bool,
  }),
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  commodityType: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ name: PropTypes.string, code: PropTypes.string })]),
};

export default Item;
